import { useState } from 'react';

import { RotateCw } from 'lucide-react';
import styled from 'styled-components';

import { Header } from 'components/Header';
import { HeaderTitle } from 'components/Header/HeaderTitle';
import { type RouteMode, ROUTE_MODES } from 'components/LeafletMap';
import { PageTitle } from 'components/PageTitle';
import { SegmentedControl } from 'components/SegmentedControl';
import { PAGES } from 'constants/pages';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { DateNavigator } from './DateNavigator';

type PlanningHeaderProps = {
    selectedDate: Date;
    onChangeDate: (date: Date) => void;
    regenerating: boolean;
    onRegenerate: (overrideManualChanges: boolean) => void;
    showRouteMode: boolean;
    routeMode: RouteMode;
    onChangeRouteMode: (mode: RouteMode) => void;
};

// Page header: title + "Régénérer le planning" (with its Standard/Complète choice dialog) + date navigation.
// The regenerate dialog open/close state is purely local UI, so it lives here, not in the page.
export const PlanningHeader = ({
    selectedDate,
    onChangeDate,
    regenerating,
    onRegenerate,
    showRouteMode,
    routeMode,
    onChangeRouteMode,
}: PlanningHeaderProps) => {
    const [regenerateChoiceOpen, setRegenerateChoiceOpen] = useState(false);

    const handleRegenerate = (overrideManualChanges: boolean) => {
        setRegenerateChoiceOpen(false);
        onRegenerate(overrideManualChanges);
    };

    return (
        <>
            <StyledHeader>
                <HeaderTitle>
                    <PageTitle page={PAGES.dynamicDeliveryPlanning} />
                </HeaderTitle>
                <HeaderActions>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setRegenerateChoiceOpen(true)}
                        disabled={regenerating}
                        data-test="dynamic-delivery-regenerate"
                    >
                        <RotateCw size={16} />
                        {regenerating ? 'Régénération…' : 'Régénérer le planning'}
                    </Button>
                    {showRouteMode && (
                        <SegmentedControl options={ROUTE_MODES} value={routeMode} onChange={onChangeRouteMode} />
                    )}
                    <DateNavigator selectedDate={selectedDate} onChange={onChangeDate} />
                </HeaderActions>
            </StyledHeader>
            <Dialog open={regenerateChoiceOpen} onOpenChange={setRegenerateChoiceOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Régénérer le planning</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm">Relancer le cron de planning dynamique pour la date affichée ?</p>
                    <div className="flex gap-2 mt-4">
                        <Button
                            variant="outline"
                            className="flex-1 h-auto py-3 flex-col items-center gap-1"
                            onClick={() => handleRegenerate(false)}
                            data-test="regenerate-standard"
                        >
                            <span>Standard</span>
                            <span className="text-xs text-muted-foreground font-normal">
                                conserve les modifications manuelles
                            </span>
                        </Button>
                        <Button
                            variant="outline"
                            className="flex-1 h-auto py-3 flex-col items-center gap-1"
                            onClick={() => handleRegenerate(true)}
                            data-test="regenerate-override"
                        >
                            <span>Complète</span>
                            <span className="text-xs text-muted-foreground font-normal">
                                écrase les modifications manuelles
                            </span>
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

const StyledHeader = styled(Header)`
    z-index: 1000;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;
